import { CommonRequestParams, CommonResponse } from 'api/api.types';
import { snakelize, camelize } from '../../../helpers/object';
import {
  GetCampaignsRequestParams,
  GetCampaignsResponse,
  GetTemporaryCampaignResponse,
  CloneToTemporaryCampaignRequestParams,
  StoreOrUpdateTemporaryCampaignRequestParams,
  GetTemporaryCampaignRequestParams,
  GetCampaignResponse,
  GetCampaignRequestParams,
  UpdateCampaignRequestParams,
  UpdateCampaignResponse,
  CreateCampaignRequestParams,
  CreateCampaignResponse,
  CloneToTemporaryCampaignResponse,
  GetHiddenInfluencersRequestParams,
  GetHiddenInfluencersResponse,
  PatchInfluencerMessageRequestParams,
} from './campaign.types';

export default (
  $axios: any,
): {
  getCampaign: (data: GetCampaignRequestParams) => Promise<GetCampaignResponse>;
  getCampaigns: (
    data: GetCampaignsRequestParams,
  ) => Promise<GetCampaignsResponse>;
  create: (
    data: CreateCampaignRequestParams,
  ) => Promise<CreateCampaignResponse>;
  updateCampaign: (
    data: UpdateCampaignRequestParams,
  ) => Promise<UpdateCampaignResponse>;
  getTemporaryCampaign: (
    data: GetTemporaryCampaignRequestParams,
  ) => Promise<GetTemporaryCampaignResponse>;
  cloneToTemporaryCampaign: (
    data: CloneToTemporaryCampaignRequestParams,
  ) => Promise<CloneToTemporaryCampaignResponse>;
  storeOrUpdateTemporaryCampaign: (
    data: StoreOrUpdateTemporaryCampaignRequestParams,
  ) => Promise<UpdateCampaignResponse>;
  clearTemporaryCampaigns: (
    data: CommonRequestParams,
  ) => Promise<CommonResponse>;
  index: any;
  store: any;
  show: any;
  getHiddenInfluencers: (
    data: GetHiddenInfluencersRequestParams,
  ) => Promise<GetHiddenInfluencersResponse>;
  patchInfluencerMessage: (
    data: PatchInfluencerMessageRequestParams,
  ) => Promise<CommonResponse>;
  delete: any;
  rules: any;
  updateQuantity: any;
  updateProductDeployAt: any;
  extendCampaignSchedule: any;
  clone: any;
  storeOrUpdateTemporary: any;
  clearTemporaries: any;
  showTemporary: any;
} => ({
  /**
   * 캠페인 조회 API
   */
  async getCampaign(
    data?: GetCampaignRequestParams,
  ): Promise<GetCampaignResponse> {
    return camelize(
      await $axios.get(
        `/campaign/${data.campaignId}`,
        snakelize({ params: data }),
      ),
    );
  },

  /**
   * 캠페인 목록 조회 API
   */
  async getCampaigns(
    data?: GetCampaignsRequestParams,
  ): Promise<GetCampaignsResponse> {
    return camelize(await $axios.get(`/campaign`, snakelize({ params: data })));
  },

  /**
   * 캠페인 생성 API
   */
  async create(
    data?: CreateCampaignRequestParams,
  ): Promise<CreateCampaignResponse> {
    return camelize(await $axios.post(`/campaign`, snakelize(data)));
  },

  /**
   * 캠페인 수정 API
   */
  async updateCampaign(
    data?: UpdateCampaignRequestParams,
  ): Promise<UpdateCampaignResponse> {
    return camelize(
      await $axios.patch(`/campaign/${data.campaign.id}`, snakelize(data)),
    );
  },

  /**
   * 임시 저장 캠페인 조회 API
   */
  async getTemporaryCampaign(
    data?: GetTemporaryCampaignRequestParams,
  ): Promise<GetTemporaryCampaignResponse> {
    return camelize(
      await $axios.get(`/campaign/temporary`, snakelize({ params: data })),
    );
  },

  /**
   * 캠페인 임시 저장으로 복제 API
   */
  async cloneToTemporaryCampaign(
    data?: CloneToTemporaryCampaignRequestParams,
  ): Promise<CloneToTemporaryCampaignResponse> {
    return camelize(await $axios.post(`/campaign/clone`, snakelize(data)));
  },

  /**
   * 캠페인 임시 저장 API
   */
  async storeOrUpdateTemporaryCampaign(
    data?: StoreOrUpdateTemporaryCampaignRequestParams,
  ): Promise<UpdateCampaignResponse> {
    return camelize(
      await $axios.post(`/campaign/store-or-update-temporary`, snakelize(data)),
    );
  },

  /**
   * 임시 저장 캠페인 삭제 API
   */
  async clearTemporaryCampaigns(
    data?: CommonRequestParams,
  ): Promise<CommonResponse> {
    return camelize(
      await $axios.post(`/campaign/clear-temporaries`, snakelize(data)),
    );
  },

  /**
   * 캠페인 목록 조회 API
   * @deprecated
   */
  async index(data?: any) {
    return camelize(await $axios.get(`/campaign`, snakelize({ params: data })));
  },

  /** 캠페인 생성 API
   * @deprecated
   */
  async store(data?: any) {
    return camelize(
      await $axios.post(`/campaign`, snakelize(data), {
        params: {
          outputType: data.campaign?.outputType?.value ?? undefined,
        },
      }),
    );
  },

  async show(data?: any) {
    return camelize(
      await $axios.get(
        `/campaign/${data.campaignId}`,
        snakelize({ params: data }),
      ),
    );
  },

  async getHiddenInfluencers(data?: any) {
    return camelize(
      await $axios.get(
        `/campaign/${data.campaignId}/product/${data.productId}/hidden-applicants?brand_token=${data.brandToken}`,
        snakelize({ params: {...data, brand_token: data.brandToken} }),
      ),
    );
  },

  async patchInfluencerMessage(data?: PatchInfluencerMessageRequestParams) {
    return camelize(
      await $axios.patch(
        `/campaign/${data.campaignId}/product/${data.productId}/applicants-messages?brand_token=${data.brandToken}`,
        // snakelize({ params: data?.messages }),
        data?.messages,
      ),
    );
  },

  async delete(data?: any) {
    await camelize(
      $axios.delete(`/campaign/${data.id}`, snakelize({ data: data })),
    );
  },

  async rules(data?: any) {
    return camelize(
      await $axios.get(`/campaign/rules`, snakelize({ params: data })),
    );
  },

  async updateQuantity(data?: any) {
    return camelize(
      await $axios.patch(
        `/campaign/${data.campaignId}/product/${data.productId}/quantity`,
        snakelize(data),
      ),
    );
  },

  async updateProductDeployAt(data?: any) {
    return camelize(
      await $axios.patch(
        `/campaign/${data.campaignId}/product/${data.productId}/deploy`,
        snakelize(data),
      ),
    );
  },

  async extendCampaignSchedule(data?: any) {
    return camelize(
      await $axios.patch(
        `/campaign/${data.campaignId}/extend`,
        snakelize(data),
      ),
    );
  },

  async clone(data?: any) {
    return camelize(await $axios.post(`/campaign/clone`, snakelize(data)));
  },

  async storeOrUpdateTemporary(data?: any) {
    return camelize(
      await $axios.post(`/campaign/store-or-update-temporary`, snakelize(data)),
    );
  },

  async clearTemporaries(data?: any) {
    return camelize(
      await $axios.post(`/campaign/clear-temporaries`, snakelize(data)),
    );
  },

  async showTemporary(data?: any) {
    return camelize(
      await $axios.get(`/campaign/temporary`, snakelize({ params: data })),
    );
  },
});
